import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import {
  FilterGroup,
  FilterUIType,
  ODataFilter
} from 'src/app/shared/components/odata-filter/models/odata-filter';
import { SelectedFilter } from 'src/app/shared/components/odata-filter/models/selected-filter';
import { combineLatest, Subscription } from 'rxjs';
import { BaseODataService } from 'src/app/shared/services/base-odata.service';
import { SavedFilter } from 'src/app/shared/components/odata-filter/models/saved-filter';

@Component({
  selector: 'app-admin-automation-odata-filter',
  templateUrl: './admin-automation-odata-filter.component.html',
  styleUrls: ['./admin-automation-odata-filter.component.scss'],
  standalone: false
})
export class AdminAutomationOdataFilterComponent implements OnInit, OnDestroy {
  @Input() filterService: BaseODataService;
  @Input() allowedFilterGroups: FilterGroup[] = [];
  @Input() allowedFilterDisplayNames: string[] = [];
  // Filters whose display names should never be offered (applied after the allow-lists).
  @Input() disallowedFilterDisplayNames: string[] = [];
  @Output() SelectedFiltersChanged = new EventEmitter<SelectedFilter[]>();
  @Input()
  set appliedFilters(value) {
    this.selectedFilters = value.slice();
  }
  selectedFilters: SelectedFilter[] = [];
  filters: ODataFilter[];
  private subs: Subscription[] = [];

  ngOnInit(): void {
    if (!this.filterService) return;
    this.subs.push(
      this.filterService.filterCollection.subscribe((results) => {
        this.filters = this.applyFilterRestrictions(results ?? []);
      })
    );
  }

  private applyFilterRestrictions(filters: ODataFilter[]): ODataFilter[] {
    const denied = new Set((this.disallowedFilterDisplayNames ?? []).map((n) => n.toLowerCase()));
    if (denied.size > 0) {
      filters = filters.filter((f) => !denied.has(f.displayName.toLowerCase()));
    }

    const groupRestrictions = this.allowedFilterGroups ?? [];
    const nameRestrictions = this.allowedFilterDisplayNames ?? [];
    const hasGroupRestrictions = groupRestrictions.length > 0;
    const hasNameRestrictions = nameRestrictions.length > 0;

    if (!hasGroupRestrictions && !hasNameRestrictions) {
      return filters;
    }

    const allowedNames = new Set(nameRestrictions.map((name) => name.toLowerCase()));

    return filters.filter((filter) => {
      const isAllowedByGroup = hasGroupRestrictions
        ? groupRestrictions.includes(filter.filterGroup)
        : false;
      const isAllowedByName = hasNameRestrictions
        ? allowedNames.has(filter.displayName.toLowerCase())
        : false;

      return isAllowedByGroup || isAllowedByName;
    });
  }

  get isValid(): boolean {
    return this.selectedFilters.findIndex((x) => !x.isValid || !x.isSubFilterValid) == -1;
  }

  get isApplied(): boolean {
    return this.selectedFilters.findIndex((x) => !x.isFilterApplied || !x.isSubFilerApplied) == -1;
  }

  protected get acceptableFilters(): SelectedFilter[] {
    return this.selectedFilters.filter((x) => x.isValid);
  }

  addFilter() {
    this.selectedFilters.push(new SelectedFilter(null));
    this.SelectedFiltersChanged.emit(this.selectedFilters);
  }

  deleteFilter(index: number) {
    this.selectedFilters.splice(index, 1);
    this.SelectedFiltersChanged.emit(this.selectedFilters);
  }

  addSubFilter(selectedFilter: SelectedFilter) {
    selectedFilter.selectedSubFilters.push(new SelectedFilter(null));
    this.SelectedFiltersChanged.emit(this.selectedFilters);
  }

  deleteSubFilter(selectedFilter: SelectedFilter, index: number) {
    selectedFilter.selectedSubFilters.splice(index, 1);
    this.SelectedFiltersChanged.emit(this.selectedFilters);
  }

  getFilterModel(): AdminAutomationOdataFilterComponentEvent {
    if (this.selectedFilters.length == 0) return { oDataFilters: [], oDataQuery: '' };
    this.selectedFilters.forEach((item) => {
      item.isFilterApplied = true;
      this.cleanupFiltersBeforApply(item);
      if (item.hasSubFilter && item.isSubFilterAdded) {
        item.selectedSubFilters.forEach((subItem) => {
          subItem.isFilterApplied = true;
          this.cleanupFiltersBeforApply(subItem);
        });
      }
    });

    if (!this.isValid) return null;
    return {
      oDataFilters: this.acceptableFilters.map((x) =>
        SavedFilter.parseToString(new SelectedFilter(x))
      ),
      oDataQuery: this.filterService.getFilterQueryForAutomation(this.selectedFilters)
    };
  }

  private cleanupFiltersBeforApply(item: SelectedFilter) {
    //to make sure we do not send any value if filter uses an operator that does not need value.
    if (item.filterOperator && !item.filterOperator.isSearchValueNeeded) {
      item.searchValue = null;
      item.searchValues = [];
    }
    if (item.uiType != FilterUIType.Multiselect) {
      item.searchValues = [];
    } else {
      item.searchValue = null;
    }
  }
  ngOnDestroy(): void {
    this.subs.forEach((sub) => sub.unsubscribe());
  }
}

export interface AdminAutomationOdataFilterComponentEvent {
  oDataFilters: string[];
  oDataQuery: string;
}
